import { ReactNode } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { StringSchema } from 'yup';

import { RelatingExpression } from '@/shared/_legacy_/condition-utils';
import { ProfileType } from '@/shared/api';
import { CountryCode } from '@/shared/types';
import { ArrayOrOne } from '@/shared/types/utility-types';
import { BtnProps } from '@/shared/uikitV2/Button';
import { FileUploaderOptions } from '@/widgets/FileUploader';

import { PickDtoContextEntity } from '../hooks/use-form-render/components/PickArrayOfDto';
import { FieldActionType } from '../hooks/useFormRenderV2/constants';
import { CollectionName, CollectionsTypeValueMap } from '../sj-orm';
import { SJDatabase } from '../sj-orm/core';
import { BaseDto } from '../sj-orm/models/base.dto';
import { QuestionType } from '../sj-orm/models/form/question.dto';

type TI18Alias = string;

type TSelectItem = {
  key: string;
  label: string;
  icon?: ReactNode;
};

export type PickDtoFilter = <K extends CollectionName, T extends CollectionsTypeValueMap[K]>(
  item: T,
  ctxEntity: PickDtoContextEntity,
  fieldName: string,
) => boolean;

export type PickDtoFilterMaker = (params: {
  fieldName: string;
  ctxEntity: PickDtoContextEntity;
  sjdb: SJDatabase;
}) => PickDtoFilter | null;

export type PickDtoIsRequired = (fieldName: string, ctxEntity: PickDtoContextEntity) => boolean;
export type PickDtoOnChange = (fieldName: string, ctxEntity: PickDtoContextEntity) => void;

// TODO: remove after moving all forms to useFormRenderV2
export type RenderNewButtonDeleteOptions = {
  mainFieldName: string;
  texts: {
    ok: string;
    cancel: string;
    description: string;
    button: string;
  };
};

export type RenderFieldsSetDeleteOptions = RenderNewButtonDeleteOptions;

export type SelectFromListOnChange = (
  fieldName: string,
  ctxEntity: PickDtoContextEntity,
  newValue: string,
  setValue: UseFormSetValue<Record<string, unknown>>,
) => void;

export type ValueType<TQuestionType extends HelperRenderType, TDtoContext, TValueType = unknown> = {
  label: TI18Alias;
  placeholder?: string;
  showIf?: RelatingExpression<TDtoContext>;
  disableIf?: RelatingExpression<TDtoContext>;
  disableIfExists?: boolean;
  yupScheme?: StringSchema;
  order?: number;
  tooltip?: string;
  equivalentValueTooltip?: string;
  withSearch?: boolean;
  // only works for default inputs and pick array of dto
  deps?: Array<string>;
  collection?: CollectionName;
  colSpan?: number;
  buttonAfter?: ButtonAfterConfig;
  capitalized?: boolean;
  hideCondition?: HideCondition;
} & (
  | { required?: true; value?: TValueType; shouldClean?: boolean }
  | { required?: false; value?: TValueType; shouldClean?: boolean }
) & {
    showInFastAdd?: boolean;
  } & ( // TODO rework [WA-325]
    | {
        type: Exclude<
          TQuestionType,
          | QuestionType.SELECT_FROM_LIST
          | QuestionType.CURRENCY_AMOUNT
          | QuestionType.TIME_DURATION
          | QuestionType.PICK_ARRAY_OF_DTO
          | QuestionType.PICK_ONE_OF_DTOS
          | QuestionType.RENDER_NEW_BUTTON
          | QuestionType.FILE_UPLOAD
          | QuestionType.DATE_TIME
          | QuestionType.ADDRESS
          | QuestionType.CO_OWNERS
          | QuestionType.LEGALIZED_COPIES
          | FormLabelItem
        >;
      }
    | QuestionDateTime
    | QuestionCurrencyAmount
    | QuestionTimeDuration
    | QuestionSelectFromList
    | QuestionPickArrayOfDto
    | QuestionPickOneOfDtos
    | QuestionFileUpload
    | QuestionRenderNewButton
    | QuestionAddress
    | QuestionCoOwners
    | QuestionLegalizedCopies
  );

//#region Questions
type QuestionDateTime = {
  type: QuestionType.DATE_TIME;
  isDateRangePair?: boolean;
  watchFieldName?: string;
};

type QuestionCurrencyAmount = {
  type: QuestionType.CURRENCY_AMOUNT;
  items: TSelectItem[];
};

type QuestionTimeDuration = {
  type: QuestionType.TIME_DURATION;
  items: TSelectItem[];
};

type QuestionSelectFromList = {
  type: QuestionType.SELECT_FROM_LIST;
  items: TSelectItem[];
  onChange?: SelectFromListOnChange;
  mode?: 'multiple' | 'tags';
};

type QuestionPickArrayOfDto = {
  type: QuestionType.PICK_ARRAY_OF_DTO;
  items: ArrayOrOne<CollectionName>; // TODO CollectionName[]
  mode?: 'multiple' | 'tags';
  dtoType?: string | string[];
  dtoCategories?: string[];
  filter?: PickDtoFilter;
  makeExtFilter?: PickDtoFilterMaker;
  isRequired?: PickDtoIsRequired;
  onChange?: PickDtoOnChange;
};

type QuestionPickOneOfDtos = {
  type: QuestionType.PICK_ONE_OF_DTOS;
  colName: CollectionName.CONTACTS | CollectionName.BENEFICIARIES | CollectionName.BENEFICIARIES;
  dtoType: string[];
  filter?: PickDtoFilter;
  makeExtFilter?: PickDtoFilterMaker;
  isRequired?: PickDtoIsRequired;
  onChange?: PickDtoOnChange;
};

type QuestionFileUpload = {
  type: QuestionType.FILE_UPLOAD;
  isMultiple?: boolean;
} & FileUploaderOptions;

// TODO: remove after moving all forms to useFormRenderV2
type QuestionRenderNewButton = {
  type: QuestionType.RENDER_NEW_BUTTON;
  label: TI18Alias;
  renderItems: string[];
  uniqKeyPrefix: string; // should be uniq for react key properties
  deleteOptions: RenderNewButtonDeleteOptions;
};

type QuestionAddress = {
  type: QuestionType.ADDRESS;
};

type QuestionCoOwners = {
  type: QuestionType.CO_OWNERS;
};

type QuestionLegalizedCopies = {
  type: QuestionType.LEGALIZED_COPIES;
};

export enum FormLabelItem {
  h1 = 'h1',
  text = 'text',
  label = 'label',
}

export const LABELS_ARR = [FormLabelItem.h1, FormLabelItem.text, FormLabelItem.label];
export const ALLOWED_LABELS_ARR = [FormLabelItem.label];

export const isLabels = (item: unknown): item is FormLabelItem =>
  LABELS_ARR.includes(<FormLabelItem>item);

export type FormItemLabelType<TDtoContext = unknown> = {
  type: FormLabelItem;
  label: string;
  order?: number;
  showIf?: RelatingExpression<TDtoContext>;
  tooltip?: string;
};

export type HelperRenderType = QuestionType | FormLabelItem;

export type OmittedBaseDto = Omit<BaseDto, 'id'>;

export type TDtoWrapper<T, THelp extends { [TK in keyof T]: HelperRenderType }> = {
  [K in keyof T]: ValueType<THelp[K], TDtoWrapper<T, THelp>, T[K]>;
};

export type Address = {
  country?: CountryCode;
  zipCode?: string;
  district?: string;
  city?: string;
  street?: string;
  buildingNumber?: string;
  apartment?: string;
};

export type HideCondition = {
  allowedProfileTypes?: ProfileType[];
};

export type FormSection = TDtoWrapper<Record<string, unknown>, Record<string, HelperRenderType>>;

export type ButtonAfterConfig = {
  action: FieldActionType;
  label: string;
  type?: BtnProps['type'];
};
